import React from "react";
import { motion } from "framer-motion";
import { Zap, Filter, Activity, Mail, X, Check, ChevronRight, ArrowRight } from "lucide-react";

const PUBLIC_DEMO = "/book/ac9c3982-f96d-4fa3-91e3-cba7234c6e88";

function CtaInline({ to, label = "Provala in azione" }) {
  return (
    <a
      href={to}
      className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
      data-testid="demo-cta-inline"
    >
      {label} <ArrowRight className="h-3 w-3" />
    </a>
  );
}

/**
 * Visual demo section for the Landing page. Static mockups of real product
 * screenshots/components — designed to *show* the unique features at a glance
 * without needing a real account.
 *
 * Each demo is a hand-crafted SVG/HTML composition that mimics the actual UI
 * but with curated demo data. No backend calls, no live state.
 */
export default function FeatureDemos() {
  return (
    <section className="bg-zinc-50 border-y border-zinc-100 dark:bg-zinc-900 dark:border-zinc-800" data-testid="feature-demos-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="max-w-2xl mb-10 md:mb-14">
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600 mb-3 dark:text-blue-400">Funzionalità che fanno la differenza</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium dark:text-zinc-100">
            Non solo un calendario.
          </h2>
          <p className="text-sm md:text-base text-zinc-600 mt-3 dark:text-zinc-400 max-w-xl">
            Ogni dettaglio è pensato per farti risparmiare tempo e <strong>aumentare il tasso di prenotazioni</strong>.
            Ecco cosa ti danno i nostri 4 super-poteri.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
          <DemoLanes />
          <DemoHealth />
          <DemoFilters />
          <DemoProposal />
        </div>
      </div>
    </section>
  );
}

const cardClass =
  "rounded-3xl bg-white border border-zinc-100 p-6 md:p-7 overflow-hidden relative dark:bg-zinc-950 dark:border-zinc-800";
const labelClass = "text-[10px] font-bold tracking-[0.2em] uppercase text-blue-600 dark:text-blue-400 mb-2 inline-flex items-center gap-1.5";
const titleClass = "font-display text-xl md:text-2xl font-medium tracking-tight dark:text-zinc-100";
const descClass = "text-sm text-zinc-600 mt-1.5 dark:text-zinc-400";

// ============================================================================
// DEMO 1 — Lane allocation: 3 overlapping appointments side-by-side
// ============================================================================
function DemoLanes() {
  const blocks = [
    { lane: 0, top: 8, h: 48, name: "Sara · Manicure", color: "bg-blue-500" },
    { lane: 1, top: 16, h: 56, name: "Lucia · Pedicure", color: "bg-violet-500" },
    { lane: 2, top: 0, h: 64, name: "Marta · Pulizia viso", color: "bg-emerald-500" },
  ];
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5 }}
      className={cardClass}
      data-testid="demo-lanes"
    >
      <div className={labelClass}><Zap className="h-3 w-3" /> Smart slot</div>
      <h3 className={titleClass}>Appuntamenti sovrapposti? Affiancati. Mai impilati.</h3>
      <p className={descClass}>Quando più operatori sono disponibili nella stessa fascia, i blocchi si distribuiscono in colonne — esattamente come Google Calendar.</p>

      <div className="mt-6 rounded-2xl bg-zinc-50 dark:bg-zinc-900/60 p-4 ring-1 ring-zinc-100 dark:ring-zinc-800">
        <div className="grid grid-cols-[40px_1fr] gap-2 h-[112px]">
          <div className="flex flex-col justify-between text-[10px] text-zinc-400 dark:text-zinc-500 tabular-nums pt-0.5">
            <span>10:00</span>
            <span>10:30</span>
            <span>11:00</span>
          </div>
          <div className="relative bg-white rounded-xl ring-1 ring-zinc-100 dark:bg-zinc-950 dark:ring-zinc-800 overflow-hidden">
            <div className="absolute inset-x-0 top-[37px] border-t border-dashed border-zinc-200 dark:border-zinc-800" />
            <div className="absolute inset-x-0 top-[74px] border-t border-dashed border-zinc-200 dark:border-zinc-800" />
            {blocks.map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 + i * 0.12, duration: 0.35 }}
                className={`absolute ${b.color} text-white text-[10px] font-medium rounded-md px-1.5 py-1 shadow-sm leading-tight`}
                style={{ top: b.top + 4, height: b.h, left: `calc(${(b.lane / 3) * 100}% + 2px)`, width: "calc(33.333% - 4px)" }}
              >
                {b.name}
              </motion.div>
            ))}
          </div>
        </div>
        <div className="mt-3 text-[11px] text-zinc-500 dark:text-zinc-400">3 operatrici · 1 fascia oraria · zero confusione</div>
      </div>
      <CtaInline to={PUBLIC_DEMO} label="Vedi una pagina pubblica reale" />
    </motion.div>
  );
}

// ============================================================================
// DEMO 2 — Health proposte with bar chart
// ============================================================================
function DemoHealth() {
  const months = [
    { m: "Dic", a: 8, d: 3 }, { m: "Gen", a: 12, d: 4 }, { m: "Feb", a: 10, d: 6 },
    { m: "Mar", a: 14, d: 3 }, { m: "Apr", a: 16, d: 5 }, { m: "Mag", a: 11, d: 2 },
  ];
  const max = Math.max(...months.map((m) => m.a + m.d));
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: 0.05 }}
      className={cardClass}
      data-testid="demo-health"
    >
      <div className={labelClass}><Activity className="h-3 w-3" /> Health proposte</div>
      <h3 className={titleClass}>Sai perché i clienti rifiutano. Davvero.</h3>
      <p className={descClass}>Tasso di accettazione, top motivi di rifiuto, andamento mensile. Per capire dove ottimizzare gli orari.</p>

      <div className="mt-6 rounded-2xl bg-zinc-50 dark:bg-zinc-900/60 p-4 ring-1 ring-zinc-100 dark:ring-zinc-800">
        <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">Accept rate</div>
            <div className="font-display text-3xl text-emerald-600 dark:text-emerald-400 tabular-nums">71%</div>
          </div>
          <div className="text-[11px] text-zinc-500 dark:text-zinc-400">71 su 100 risposte negli ultimi 6 mesi</div>
        </div>
        <div className="flex items-end justify-between gap-1.5 h-20">
          {months.map((m, i) => {
            const total = m.a + m.d;
            const totalH = Math.max(8, Math.round((total / max) * 76));
            const aH = Math.round((m.a / total) * totalH);
            const dH = totalH - aH;
            return (
              <motion.div
                key={m.m}
                initial={{ height: 0 }}
                whileInView={{ height: totalH }}
                viewport={{ once: true }}
                transition={{ delay: 0.15 + i * 0.08, duration: 0.45, ease: "easeOut" }}
                className="flex-1 flex flex-col items-center"
              >
                <div className="flex-1 flex flex-col-reverse w-full max-w-[28px] mx-auto rounded-md overflow-hidden">
                  <div className="bg-emerald-500 dark:bg-emerald-400" style={{ height: aH }} />
                  <div className="bg-rose-500 dark:bg-rose-400" style={{ height: dH }} />
                </div>
              </motion.div>
            );
          })}
        </div>
        <div className="flex items-end justify-between mt-1.5 text-[10px] text-zinc-400 dark:text-zinc-500">
          {months.map((m) => <span key={m.m} className="flex-1 text-center font-semibold">{m.m}</span>)}
        </div>
        <div className="mt-3 rounded-lg bg-rose-100/70 dark:bg-rose-950/30 px-3 py-1.5 text-[11px] text-rose-700 dark:text-rose-300">
          <strong>Top rifiuto:</strong> "Vacanza" (43%) — i tuoi clienti sono spesso fuori
        </div>
      </div>
      <CtaInline to="/demo#health" label="Esplora Health proposte" />
    </motion.div>
  );
}

// ============================================================================
// DEMO 3 — Smart filters: chip-bar with active filters + agenda hint
// ============================================================================
function DemoFilters() {
  const filters = [
    { label: "Cancellati nascosti", color: "bg-zinc-900 dark:bg-zinc-100" },
    { label: "No-show nascosti", color: "bg-zinc-900 dark:bg-zinc-100" },
    { label: "Sara", color: "bg-zinc-900 dark:bg-zinc-100", dot: "#10B981" },
  ];
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: 0.1 }}
      className={cardClass}
      data-testid="demo-filters"
    >
      <div className={labelClass}><Filter className="h-3 w-3" /> Filtri agenda</div>
      <h3 className={titleClass}>Mostra solo quello che ti interessa.</h3>
      <p className={descClass}>Nascondi cancellati, no-show, completati. Filtra per operatore. Mostra solo conflitti. Combinabili in un click.</p>

      <div className="mt-6 rounded-2xl bg-zinc-50 dark:bg-zinc-900/60 p-4 ring-1 ring-zinc-100 dark:ring-zinc-800">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-400 dark:text-zinc-500">Filtri attivi</span>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {filters.map((f, i) => (
            <motion.span
              key={f.label}
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 + i * 0.1 }}
              className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11px] font-medium text-white ${f.color} dark:text-zinc-900`}
            >
              {f.dot && <span className="h-2 w-2 rounded-full" style={{ background: f.dot }} />}
              {f.label}
              <span className="h-4 w-4 rounded-full bg-white/20 dark:bg-zinc-900/15 inline-flex items-center justify-center"><X className="h-2.5 w-2.5" /></span>
            </motion.span>
          ))}
        </div>
        <div className="space-y-1.5">
          {[
            { time: "09:00", who: "Giulia · Taglio", color: "bg-blue-500" },
            { time: "10:00", who: "Sara · Manicure", color: "bg-emerald-500" },
            { time: "11:30", who: "Marta · Massaggio", color: "bg-violet-500" },
          ].map((row, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.45 + i * 0.08 }}
              className="flex items-center gap-2 bg-white dark:bg-zinc-950 rounded-lg px-2.5 py-1.5 ring-1 ring-zinc-100 dark:ring-zinc-800"
            >
              <span className="text-[11px] font-medium text-zinc-500 dark:text-zinc-400 tabular-nums w-12">{row.time}</span>
              <span className={`h-1.5 w-1.5 rounded-full ${row.color}`} />
              <span className="text-[11px] text-zinc-700 dark:text-zinc-300 truncate">{row.who}</span>
            </motion.div>
          ))}
        </div>
      </div>
      <CtaInline to="/demo#filtri" label="Prova i filtri interattivi" />
    </motion.div>
  );
}

// ============================================================================
// DEMO 4 — Proposal with confirm/decline + alternative slots
// ============================================================================
function DemoProposal() {
  const slots = ["10:00", "10:30", "11:00", "11:30", "14:00", "14:30", "15:00", "15:30"];
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: 0.15 }}
      className={cardClass}
      data-testid="demo-proposal"
    >
      <div className={labelClass}><Mail className="h-3 w-3" /> Proposte intelligenti</div>
      <h3 className={titleClass}>Il cliente sceglie. Tu non rincorri più.</h3>
      <p className={descClass}>Quando proponi uno slot, il cliente può confermare, rifiutare con motivo, o scegliere un orario alternativo che gli va bene.</p>

      <div className="mt-6 rounded-2xl bg-zinc-50 dark:bg-zinc-900/60 p-4 ring-1 ring-zinc-100 dark:ring-zinc-800">
        <div className="rounded-xl bg-white dark:bg-zinc-950 px-4 py-3 ring-1 ring-zinc-100 dark:ring-zinc-800">
          <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-blue-600 dark:text-blue-400">Proposta da Studio Belle</div>
          <div className="font-display text-base mt-0.5 dark:text-zinc-100">Ven 15 mag, 16:30 · Manicure classica</div>
          <div className="text-[11px] text-zinc-500 dark:text-zinc-400">45 min · €30</div>
          <div className="grid grid-cols-2 gap-1.5 mt-3">
            <button type="button" className="h-8 rounded-full bg-blue-600 text-white text-[11px] font-semibold inline-flex items-center justify-center gap-1 cursor-default" tabIndex={-1}>
              <Check className="h-3 w-3" /> Conferma
            </button>
            <button type="button" className="h-8 rounded-full bg-zinc-100 text-zinc-700 text-[11px] font-semibold dark:bg-zinc-800 dark:text-zinc-300 cursor-default" tabIndex={-1}>
              Vedi altri orari
            </button>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.45, duration: 0.4 }}
          className="mt-2.5 rounded-xl bg-white dark:bg-zinc-950 px-3 py-2.5 ring-1 ring-zinc-100 dark:ring-zinc-800"
        >
          <div className="text-[10px] font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400 mb-1.5">Sabato 16 maggio</div>
          <div className="grid grid-cols-4 gap-1">
            {slots.map((s, i) => (
              <motion.button
                key={s}
                type="button"
                tabIndex={-1}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.55 + i * 0.04 }}
                className={`h-7 rounded-md text-[10px] font-medium tabular-nums cursor-default ${s === "10:30" ? "bg-blue-600 text-white" : "border border-zinc-200 text-zinc-700 dark:border-zinc-700 dark:text-zinc-300"}`}
              >
                {s}
              </motion.button>
            ))}
          </div>
          <div className="mt-2.5 inline-flex items-center gap-1 text-[10px] text-blue-600 font-semibold dark:text-blue-400">
            Conferma alle 10:30 <ChevronRight className="h-2.5 w-2.5" />
          </div>
        </motion.div>
      </div>
      <CtaInline to={PUBLIC_DEMO} label="Vedi un esempio reale" />
    </motion.div>
  );
}
